// Keeps the screen in step with the URL on back/forward, where Turbo alone
// does not.
//
// 1. Entries Turbo did not write. Turbo restores only the history entries it
//    wrote (history.state.turbo). Entries written by anything else are
//    nobody's once their writer is gone: a React Router tab click leaves one
//    behind, and after navigating away from OrgNav a back to it changes the
//    URL but not the screen. Those are visited here. React Router keeps its
//    own entries while OrgNavApp is mounted, and the browser keeps same-page
//    (hash-only) entries.
//
// 2. A back right after a frame navigation. A frame that writes history
//    (data-turbo-action) is followed by a page visit, and only when that
//    visit has rendered does Turbo take the new page for the one shown. A
//    back before then looks to Turbo like a restore of the page already
//    shown, so it renders nothing. That entry is visited here too.
//
// 3. A frame navigation that lands after a back. The frame writes history
//    before it renders. A back in between is the same restore of the page
//    already shown as in 2, and it is not seen here: the frame has not
//    rendered yet. The frame then renders, and the page visit that follows
//    leaves the frame showing one URL under another. That is put right when
//    the visit has loaded.
//
// 4. A frame navigation still loading at a back. Turbo restores the entry
//    gone back to, but nothing stops the frame: when its response arrives it
//    writes history and its page visit follows, over the restore or instead
//    of it, and the URL of the frame ends up over the restored page. The back
//    wins here: the frame navigation is dropped.
//
// 2 and 3 read Turbo's bookkeeping (Turbo.session.history.location and
// Turbo.session.view.lastRenderedLocation), and 4 resets the frame
// controller's action and fetchResponseLoaded; none of it is public API. The
// browser examples in spec/features/turbo_navigation_history_spec.rb are the
// guard when Turbo is upgraded.
const ORG_NAV_APP = '[data-react-island-component-value="Glassfrog.components.OrgNavApp"]'
const ORG_NAV_PATH = /^\/organizations\/[^/]+\/orgnav(\/|$)/

function reactRouterOwns(location) {
  return ORG_NAV_PATH.test(location.pathname) && Boolean(document.querySelector(ORG_NAV_APP))
}

function samePage(location, other) {
  return Boolean(location) && Boolean(other)
    && location.pathname === other.pathname && location.search === other.search
}

function nobodyShows(location) {
  return !reactRouterOwns(location) && !samePage(location, Turbo.session.history.location)
}

// True from the moment a frame has written history until the page visit that
// follows it has rendered.
let frameAheadOfPage = false

function noteFrameLoad() {
  const {history, view} = Turbo.session

  frameAheadOfPage = Boolean(history.location) && Boolean(view.lastRenderedLocation)
    && !samePage(history.location, view.lastRenderedLocation)
}

function notePageRender() {
  frameAheadOfPage = false
}

function turboSkips(location) {
  const skips = frameAheadOfPage && samePage(location, Turbo.session.view.lastRenderedLocation)
  frameAheadOfPage = false

  return skips
}

const HISTORY_FRAME = 'turbo-frame[data-turbo-action]'

// The frame whose request has started and has not written history yet.
let frameLoading = null
const droppedFrames = new WeakSet()

function noteFrameRequest(event) {
  if (!event.target.matches?.(HISTORY_FRAME))
    return

  frameLoading = event.target
  droppedFrames.delete(event.target)
}

function noteFrameRequestError(event) {
  if (event.target === frameLoading)
    frameLoading = null
}

// The frame has written history by now, unless it was dropped. A dropped
// frame keeps what it shows, so the restore does not cache the page it left
// with the content of the one it was going to.
function skipDroppedFrameRender(event) {
  if (event.target === frameLoading)
    frameLoading = null

  if (droppedFrames.delete(event.target))
    event.detail.render = () => {}
}

function dropFrameNavigationStillLoading() {
  if (!frameLoading)
    return

  frameLoading.delegate.action = null
  frameLoading.delegate.fetchResponseLoaded = () => Promise.resolve()
  droppedFrames.add(frameLoading)
  frameLoading = null
}

// Turbo added its popstate listener first, on import, so it has already run.
function visitEntryLeftUnshown(event) {
  dropFrameNavigationStillLoading()

  const unshown = event.state?.turbo ? turboSkips(window.location) : nobodyShows(window.location)

  if (unshown)
    Turbo.visit(window.location.href, {action: 'replace'})
}

function frameShowsAnotherPage() {
  const frame = document.querySelector('turbo-frame[data-turbo-action][src]')

  return Boolean(frame) && !samePage(new URL(frame.getAttribute('src'), window.location.href), window.location)
}

function visitPageBehindFrame() {
  if (frameShowsAnotherPage())
    Turbo.visit(window.location.href, {action: 'replace'})
}

function setupHistorySync() {
  document.addEventListener('turbo:before-fetch-request', noteFrameRequest)
  document.addEventListener('turbo:fetch-request-error', noteFrameRequestError)
  document.addEventListener('turbo:before-frame-render', skipDroppedFrameRender)
  document.addEventListener('turbo:frame-load', noteFrameLoad)
  document.addEventListener('turbo:render', notePageRender)
  document.addEventListener('turbo:load', visitPageBehindFrame)
  window.addEventListener('popstate', visitEntryLeftUnshown)
}

export default {setupHistorySync}
